import { Stack } from '@fluentui/react';
import { Button, Checkbox, TextField } from '@h2oai/ui-kit';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useDebouncedCallback } from '../../../../utils/hooks';
import { StringRegExFilterProps } from './types';

export const StringRegExFilter = memo((props: StringRegExFilterProps) => {
  const { onChange, value, inputLabel, key, disabled } = props;
  const safeValue = value ?? { inputData: '', isRegEx: false };
  const inputData = typeof safeValue.inputData === 'string' ? safeValue.inputData : '';
  const [localInput, setLocalInput] = useState(inputData);
  const hasInitialized = useRef(false);

  const generateReadableValue = useCallback(
    (inputData?: string, isRegEx?: boolean) => {
      if (!inputData) return '';
      const type = isRegEx ? 'RegEx' : 'Exact';
      return `${inputLabel || 'Filter'}: ${inputData} (${type})`;
    },
    [inputLabel]
  );

  const debouncedOnChange = useDebouncedCallback((inputData?: string) => {
    const newValue = { ...safeValue, inputData };
    const readableValue = generateReadableValue(inputData || '', safeValue.isRegEx);
    onChange(newValue, readableValue);
  }, 400);

  useEffect(() => {
    setLocalInput(inputData);
  }, [inputData]);

  useEffect(() => {
    if (safeValue.inputData && !hasInitialized.current) {
      const readableValue = generateReadableValue(safeValue.inputData, safeValue.isRegEx);
      onChange(safeValue, readableValue);
      hasInitialized.current = true;
    }
  }, [safeValue.inputData, safeValue.isRegEx, generateReadableValue, onChange]);

  const handleInputChange = useCallback(
    (_e: any, inputData?: string) => {
      const value = inputData ?? '';
      setLocalInput(value);
      debouncedOnChange(value);
    },
    [debouncedOnChange]
  );

  const handleRegExToggle = useCallback(() => {
    const newValue = { ...safeValue, isRegEx: !safeValue.isRegEx };
    const readableValue = generateReadableValue(safeValue.inputData, newValue.isRegEx);
    onChange(newValue, readableValue);
  }, [safeValue, generateReadableValue, onChange]);

  return (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
      <TextField
        onChange={disabled ? undefined : handleInputChange}
        value={localInput}
        label={inputLabel}
        disabled={disabled}
      />

      <Button onClick={disabled ? undefined : handleRegExToggle} disabled={disabled}>
        <Checkbox
          id={`toggle-${key}-regex-events`}
          key={`toggle-${key}-regex-events`}
          styles={{ root: { pointerEvents: 'none' } }}
          checked={safeValue.isRegEx}
        />
        RegEx
      </Button>
    </Stack>
  );
});
